import { useContext } from 'react'

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faCheckCircle, faTimesCircle } from '@fortawesome/pro-light-svg-icons'

import {
  Button, ModalContext, SidebarModal,
} from '@domain-group/suit-theme'

import { confirmAction, formatEntityCount } from '@functions/entity'
import type { HandleCallbackActionParams } from '@functions/handleCallbackAction'

import type { InvalidPrice } from '@hooks/useCampaignQuote'

import type { CampaignContributionModel } from '@models/types'

import useCampaignQuote from '@hooks/useCampaignQuote'
import InvalidPricesListItem from './components/InvalidPricesListItem'
import InvalidPricesListItemHeader from './components/InvalidPricesListItemHeader'

import useBulkUpdateProviderPriceListsModal from './hooks/useBulkUpdateCampaignQuoteInvalidPricesModal'

const MODAL_KEY = 'BulkUpdateCampaignQuoteInvalidPricesModal'

type ModalContextTypes = {
  baseModalKey: string,
  modalData: {
    campaignContribution: Partial<CampaignContributionModel>,
  },
}

type ModalCallbacks = {
  bulkUpdateProviderPriceList: (payload: HandleCallbackActionParams) => void,
  closeModal: () => void,
}

type ModalProps = {
  callbacks: ModalCallbacks,
  showModal: boolean,
}

const BulkUpdateCampaignQuoteInvalidPricesModal = (props: ModalProps) => {
  const { callbacks, showModal } = props
  const { closeModal } = callbacks || {}

  const modalContext = useContext<ModalContextTypes>(ModalContext)
  const { modalData } = modalContext

  // @ts-expect-error TS7053
  const modalPayload = modalData?.[MODAL_KEY] || {}

  const { campaignQuote } = modalPayload

  const campaignQuotePayload = useCampaignQuote(campaignQuote)
  const {
    callbacks: {
      bulkUpdateCampaignQuoteItemsPrices: bulkFn,
      validateCampaignQuotePrices: validateFn,
    },
    updating,
  } = campaignQuotePayload

  const invalidPrices = campaignQuote?.invalidItems || []

  const bulkUpdateProviderPriceListPayload = useBulkUpdateProviderPriceListsModal({
    invalidPrices,
  })

  const {
    allSelected,
    callbacks: {
      deselectAllItems,
      selectAllItems,
      toggleId,
    },
    hasSelectedEntities,
    initSelectedIds,
    selectedEntityIds,
  } = bulkUpdateProviderPriceListPayload

  const bulkUpdateCampaignQuoteItemsPricesPayload = {
    callbacks: {
      actionFn: bulkFn,
      afterAction: () => {
        validateFn()
        closeModal()
      },
    },
    options: {
      requestOptions: {
        include_campaign_quote_items: true,
      },
      entityParams: selectedEntityIds,
      confirmButtonColor: '#43ac6a',
      icon: 'success',
    },
    strings: {
      actionString: 'update',
      confirmString: `Order Items with quantities or options no longer available on price
                      list will be excluded from bulk price update`,
      entityName: 'Prices',
      toastText: 'Prices Updated',
    },
  }

  return (
    <SidebarModal
      callbacks={callbacks}
      clickSafeZone
      modalKey={MODAL_KEY}
      showModal={showModal}
      size="large"
    >
      <SidebarModal.Header
        callbacks={callbacks}
        title="Bulk Update"
        titleSecondLine="Prices"
      />

      <SidebarModal.Content>
        <InvalidPricesListItemHeader />

        {invalidPrices?.map((invalidPrice: InvalidPrice, idx: number) => (
          <InvalidPricesListItem
            callbacks={{
              toggleId: () => toggleId(invalidPrice?.campaign_quote_item_id!),
              validateFn: () => validateFn(),
            }}
            checked={selectedEntityIds.includes(invalidPrice?.campaign_quote_item_id!)}
            invalidPrice={invalidPrice}
            key={invalidPrice?.campaign_quote_item_id}
            lastItem={idx === invalidPrices.length - 1}
          />
        ))}
      </SidebarModal.Content>

      <SidebarModal.Footer>
        <Button
          buttonStyle="secondaryUtility"
          icon={<FontAwesomeIcon icon={allSelected ? faTimesCircle : faCheckCircle} />}
          // @ts-expect-error TS2322
          marginRight="medium"
          disabled={!initSelectedIds.length}
          marginLeft="auto"
          onClick={allSelected
            ? () => deselectAllItems()
            : () => selectAllItems()}
          size="large"
          width="fit-content"
        >
          {allSelected ? 'Deselect All' : 'Select All'}
        </Button>

        <Button
          buttonStyle="primaryCreate"
          disabled={!hasSelectedEntities}
          loading={updating}
          loadingBubbleColor="white"
          // @ts-expect-error TS2345
          onClick={() => confirmAction(bulkUpdateCampaignQuoteItemsPricesPayload)}
          size="large"
          // @ts-expect-error TS2322
          width="fit-content"
        >
          {selectedEntityIds.length
            ? `Update ${formatEntityCount({ entityString: 'Price', entityCount: selectedEntityIds.length })}`
            : 'Update Prices'}
        </Button>
      </SidebarModal.Footer>
    </SidebarModal>
  )
}

const LazyLoadedModal = (props: ModalProps) => (
  <SidebarModal.RenderController {...props}>
    <BulkUpdateCampaignQuoteInvalidPricesModal {...props} />
  </SidebarModal.RenderController>
)

export default LazyLoadedModal
